import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Briefcase, Users, TreePine, CheckCircle2, Calendar } from "lucide-react";
import { ProviderBranding } from "@/components/ProviderBranding";
import { CrewManager } from "@/components/CrewManager";
import { TurfManager } from "@/components/TurfManager";
import { TurfLoops } from "@/components/TurfLoops";
import { MonthlyBlueprint } from "@/components/MonthlyBlueprint";

const kpiMeta = [
  { key: "open_jobs", label: "Open jobs", icon: Briefcase },
  { key: "clients", label: "Client accounts", icon: Users },
  { key: "trees_managed", label: "Trees managed", icon: TreePine },
  { key: "completed_jobs", label: "Completed", icon: CheckCircle2 },
];

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get("/provider/dashboard").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (ticket, idx, done) => {
    await api.post(`/provider/tickets/${ticket.id}/checklist`, { item_index: idx, done });
    load();
  };
  const complete = async (ticket) => {
    await api.post(`/provider/tickets/${ticket.id}/complete`);
    toast.success("Job marked complete");
    load();
  };

  if (!data) return <div className="min-h-screen"><Navbar /><p className="p-10 font-serif text-2xl text-stone-400">Loading job board…</p></div>;

  const openTickets = data.tickets.filter((t) => t.status === "open");
  const whiteLabel = user.plan === "CrewEnterprise";
  const isOwner = !user.crew_owner_id;

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-emerald-700">Landcare operations</span>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-emerald-950">{whiteLabel && user.company ? user.company : "Crew job board"}</h1>
            {whiteLabel && <Badge className="mt-2 rounded-full bg-amber-100 text-amber-700 border-amber-200">White-label · {user.plan}</Badge>}
          </div>
        </div>

        {/* KPI row */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiMeta.map((k, i) => (
            <motion.div key={k.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
              <k.icon className="w-6 h-6 text-emerald-700" />
              <div className="mt-3 font-serif text-4xl text-emerald-950">{data.kpis[k.key]}</div>
              <div className="text-xs uppercase tracking-[0.15em] text-stone-400">{k.label}</div>
            </motion.div>
          ))}
        </div>

        {isOwner && (
          <div className="mt-8 space-y-8">
            <ProviderBranding />
            <CrewManager />
          </div>
        )}

        <div className="mt-8">
          <TurfManager clients={data.clients} />
        </div>

        <div className="mt-8">
          <TurfLoops clients={data.clients} />
        </div>

        {isOwner && (
          <div className="mt-8">
            <MonthlyBlueprint />
          </div>
        )}

        <div className="mt-8 grid lg:grid-cols-4 gap-6">
          {/* Job board */}
          <div className="lg:col-span-3">
            <h2 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-700" /> Dispatched work tickets</h2>
            <div className="mt-4 space-y-4">
              {openTickets.length === 0 && <p className="text-sm text-stone-400 rounded-2xl border border-dashed border-stone-300 bg-white p-10 text-center">No open jobs. Milestones will appear here automatically.</p>}
              {openTickets.map((t) => (
                <motion.div key={t.id} data-testid={`ticket-${t.id}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <span className="text-xs uppercase tracking-[0.2em] text-stone-400">{t.phase}</span>
                      <h3 className="font-serif text-2xl text-emerald-950">{t.title}</h3>
                      <p className="text-sm text-stone-500">{t.consumer_name} · {t.tree_name} ({t.tree_type})</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono-ag text-xs text-stone-400">Due {t.due_date}</p>
                      <Button data-testid={`complete-${t.id}`} size="sm" onClick={() => complete(t)} className="mt-2 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Complete job</Button>
                    </div>
                  </div>
                  {t.image_url && <img src={t.image_url.startsWith("data:") ? t.image_url : `${API}${t.image_url.replace(/^\/api/, "")}?auth=${localStorage.getItem("ag_token")}`} alt="leaf report" className="mt-4 rounded-xl h-40 object-cover" />}
                  {t.note && <p className="mt-2 text-sm italic text-stone-500">“{t.note}”</p>}
                  {t.ai_diagnosis && (
                    <div data-testid={`ticket-diagnosis-${t.id}`} className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-emerald-700">AI diagnosis</p>
                      <p className="mt-1 text-sm font-semibold text-emerald-950">{t.ai_diagnosis.likely_issue} <span className="text-xs font-normal text-stone-500">· {t.ai_diagnosis.category} · {t.ai_diagnosis.confidence} confidence</span></p>
                      <p className="mt-1 text-sm text-stone-600">{t.ai_diagnosis.summary}</p>
                    </div>
                  )}
                  <div className="mt-4 space-y-2">
                    {(t.checklist || []).map((c, i) => (
                      <label key={i} className="flex items-center gap-3 text-sm text-stone-700 cursor-pointer">
                        <Checkbox data-testid={`check-${t.id}-${i}`} checked={c.done} onCheckedChange={(v) => toggle(t, i, !!v)} />
                        <span className={c.done ? "line-through text-stone-400" : ""}>{c.label}</span>
                      </label>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Clients */}
          <div>
            <h2 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Users className="w-5 h-5 text-emerald-700" /> Clients</h2>
            <div className="mt-4 space-y-3">
              {data.clients.length === 0 && <p className="text-sm text-stone-400">No client accounts yet.</p>}
              {data.clients.map((c) => (
                <div key={c.id} data-testid={`client-${c.id}`} className="rounded-xl border border-black/5 bg-white p-4 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                  <p className="font-semibold text-emerald-950">{c.name}</p>
                  <p className="text-xs text-stone-500">{c.email}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <Badge className="rounded-full bg-emerald-100 text-emerald-700 border-emerald-200">{c.plan}</Badge>
                    <span className="text-xs text-stone-400">{c.tree_count} trees</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
